import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Shield, 
  Lock, 
  Eye, 
  Bell, 
  Server, 
  Cloud, 
  Database,
  CheckCircle,
  ArrowRight,
  Users,
  Clock,
  AlertTriangle,
  FileCheck
} from 'lucide-react';

interface PulseSecurityProps {
  setCurrentPage: (page: string) => void;
}

const PulseSecurity: React.FC<PulseSecurityProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Essential Protection",
      description: "Core security features to protect your business from common cyber threats.",
      items: [
        "Firewall protection",
        "Endpoint security",
        "Email security",
        "Basic threat monitoring"
      ]
    },
    {
      icon: Lock,
      title: "Access Control",
      description: "Secure authentication and access management for your systems.",
      items: [
        "User authentication",
        "Permission management",
        "Password policies",
        "Session control"
      ]
    },
    {
      icon: Eye,
      title: "Threat Detection",
      description: "Identify potential security threats before they impact your business.",
      items: [
        "Malware detection",
        "Suspicious activity alerts",
        "Vulnerability scanning",
        "Security reporting"
      ]
    },
    {
      icon: Bell,
      title: "Security Alerts",
      description: "Stay informed about security events affecting your systems.",
      items: [
        "Real-time notifications",
        "Threat categorization",
        "Alert management",
        "Response guidance"
      ]
    }
  ];

  const services = [
    {
      icon: Server,
      title: "Infrastructure Security",
      description: "Protect your IT infrastructure with essential security measures.",
      items: [
        "Network protection",
        "Server security",
        "Device management",
        "Security updates"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      description: "Secure your cloud applications and data with comprehensive protection.",
      items: [
        "Cloud access security",
        "Data protection",
        "Configuration management",
        "Compliance monitoring"
      ]
    },
    {
      icon: Database,
      title: "Data Protection",
      description: "Safeguard your sensitive business and customer data.",
      items: [
        "Data encryption",
        "Access controls",
        "Backup security",
        "Data loss prevention"
      ]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Affordable Protection",
      description: "Cost-effective security solutions for small to medium businesses"
    },
    {
      icon: Users,
      title: "Expert Support",
      description: "Access to security professionals for guidance and assistance"
    },
    {
      icon: Clock,
      title: "Quick Deployment",
      description: "Rapid implementation with minimal disruption to operations"
    },
    {
      icon: AlertTriangle,
      title: "Risk Reduction",
      description: "Significantly reduce your exposure to common cyber threats"
    }
  ];

  const complianceFeatures = [
    {
      icon: FileCheck,
      title: "Compliance Essentials",
      items: [
        "Basic compliance reporting",
        "Security policy templates",
        "Regulatory guidance",
        "Audit preparation"
      ]
    },
    {
      icon: Shield,
      title: "Security Standards",
      items: [
        "Industry best practices",
        "Security frameworks",
        "Control implementation",
        "Security baselines"
      ]
    },
    {
      icon: Eye,
      title: "Risk Management",
      items: [
        "Risk assessment",
        "Vulnerability management",
        "Threat mitigation",
        "Security planning"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Zap className="h-16 w-16 text-blue-400 animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Pulse Cybersecurity Solution</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Essential security features for small to medium businesses, providing core protection against common cyber threats
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Services</h2>
            <p className="text-lg text-muted">
              Essential security services to protect your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
            <p className="text-lg text-muted">
              Why choose Pulse Cybersecurity Solution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Compliance Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Compliance Features</h2>
            <p className="text-lg text-muted">
              Essential compliance capabilities for your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {complianceFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
                </div>

                <ul className="space-y-3">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Business?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our security experts to learn more about our Pulse Cybersecurity Solution and how it can protect your business
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PulseSecurity;